import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Tooltips and state across various d3 charts in a React dashboard!",
  "description": "",
  "published": "2019-01-28T00:00:00.000Z",
  "redirect_from": ["/blog/tooltips-and-state-across-various-d3-charts-in-a-react-dashboard/swizec/8904"],
  "categories": "Front End, Livecoding, Technical",
  "hero": "./img/techletter.app-screenshot-1548697227934.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`One of the most common burning questions readers ask 👉 `}<em parentName="p">{`"How do I add tooltips to my charts?"`}</em>{` followed by `}<em parentName="p">{`"How do I sync them between multiple charts?"`}</em></p>
    <p>{`So we played around and got that working on a stream 👇`}</p>
    <lite-youtube {...{
      "videoid": "xyyI6EytvZk",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Using hooks because why not. It's fun to experiment with the future :)`}</p>
    <p>{`We built a simple dashboard with 3 scatterplots because scatterplots are easy to build. Filled with random data because real small datasets are hard to find.`}</p>
    <p><img parentName="p" {...{
        "src": "/1aa3fb5d20e299a630b9a899cc3bd319/hi9Ipxl.gif",
        "alt": "Scatterplots with synchronized tooltips"
      }}></img></p>
    <p>{`Scatterplots with synchronized tooltips`}</p>
    <p>{`Here's a CodeSandbox, try it out 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/nr2z3qlm00",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <h2 {...{
      "id": "first-you-need-a-context-and-some-state"
    }}>{`First, you need a context and some state`}</h2>
    <p>{`We're using context to share state between charts. No prop passing, just state that everyone has access to. It's like a Redux or MobX store without the setup.`}</p>
    <p>{`A couple lines is all we need:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/useTooltip.js

import React, { useState } from "react";

const tooltipContext = React.createContext();

function useTooltip() {
  const [tooltip, setTooltip] = useState(false);

  return { tooltip, setTooltip };
}

export { useTooltip, tooltipContext };
`}</code></pre>
    <p>{`Not sure yet how I feel about this state pattern with hooks, need to play around some more. Here's the idea:`}</p>
    <ol>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`tooltipContext`}</inlineCode></li>
      <li parentName="ol">{`Create a custom `}<inlineCode parentName="li">{`useTooltip`}</inlineCode>{` hook with 1 piece of state, the tooltip value`}</li>
      <li parentName="ol">{`Return value and setter from the hook`}</li>
      <li parentName="ol">{`Export hook and context`}</li>
    </ol>
    <p>{`Might look cleaner to export context in the `}<inlineCode parentName="p">{`useTooltip`}</inlineCode>{` hook itself? 🤔`}</p>
    <p>{`Either way, the idea here is that every component has access to the same tooltip state. It tells them whether to display a tooltip and its context. Any component can take over, usually on mouse over, and call `}<inlineCode parentName="p">{`setTooltip`}</inlineCode>{` to set a new tooltip.`}</p>
    <p>{`If your system has just 1 mouse cursor, like most computers do, this approach works great. If you want to support multiple simultaneous mouse over events, you could run into trouble.`}</p>
    <h2 {...{
      "id": "second-you-need-a-scatterplot"
    }}>{`Second, you need a scatterplot`}</h2>
    <p>{`Like I said, we used scatterplots because they're quick to build. Take some data, pick two dimensions, iterate and draw circles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/Scatterplot.js

import { tooltipContext } from "./useTooltip";

export default ({
  x,
  y,
  width,
  height,
  data,
  xDimension,
  yDimension,
  padding = 10
}) => {
  const { tooltip, setTooltip } = useContext(tooltipContext);

  const xScale = d3
    .scaleLinear()
    .domain(d3.extent(data, xDimension))
    .range([padding, width - padding]);
  const yScale = d3
    .scaleLinear()
    .domain(d3.extent(data, yDimension))
    .range([padding, height - padding]);

  return (
    <g transform="{\`translate(\${x}," \${y})\`}="">
      {data.map(d => (
        <circle key={d.id} cx={xScale(xDimension(d))} cy={yScale(yDimension(d))} r={3} onmouseover="{()" ==""> setTooltip(d)}
          onMouseOut={() => setTooltip(false)}
        />
      ))}
      {tooltip && (
        <tooltip x={xScale(xDimension(tooltip))} y={yScale(yDimension(tooltip))} info={tooltip}>
      )}
    </tooltip></circle></g>
  );
};
`}</code></pre>
    <p>{`We use the `}<inlineCode parentName="p">{`useContext`}</inlineCode>{` hook to get access to our tooltip state. Here's where using `}<inlineCode parentName="p">{`useTooltip`}</inlineCode>{` would be nicer but I couldn't quite figure out how to make it work.`}</p>
    <p>{`That gives us `}<inlineCode parentName="p">{`tooltip`}</inlineCode>{` and `}<inlineCode parentName="p">{`setTooltip`}</inlineCode>{`. I'll show you how they get into our context in a bit.`}</p>
    <p>{`Then we set up two D3 scales: One for horizontal coordinates, `}<inlineCode parentName="p">{`xScale`}</inlineCode>{`, one for vertical coordinates, `}<inlineCode parentName="p">{`yScale`}</inlineCode>{`.`}</p>
    <p>{`We return a grouping element using the `}<inlineCode parentName="p">{`transform`}</inlineCode>{` prop to position at `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{`, iterate over our data, and render circles.`}</p>
    <p>{`Each circle gets a position based on our scale and a passed-in value accessor. `}<inlineCode parentName="p">{`xDimension`}</inlineCode>{` or `}<inlineCode parentName="p">{`yDimension`}</inlineCode>{`. This lets us re-use the same scatterplot for random looks into our data you'll see. `}<inlineCode parentName="p">{`onMouseOver`}</inlineCode>{` we set the tooltip to current datapoint, `}<inlineCode parentName="p">{`onMouseOut`}</inlineCode>{` we reset it back to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <p>{`Then we render the \`\`component.`}</p>
    <h2 {...{
      "id": "third-you-need-a"
    }}>{`Third, you need a\`\``}</h2>
    <p>{`The \``}<inlineCode parentName="p">{`component takes coordinates and content. Renders them in an SVG `}</inlineCode>{`foreignObject\` so we can leverage HTML's built-in text formatting abilities.`}</p>
    <p>{`Reflowing text in SVG yourself is painful 😉`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/Scatterplot.js

const Tooltip = ({ x, y, info }) => (
  <foreignobject x="{x" +="" 10}="" y="{y" width={100} height={50}>
    <div>
      <strong>{info.name}</strong>

        {info.state} {info.zipCode}

      <p style={{ color: info.color }}>
        {info.number1.toFixed(2)}, {info.number2.toFixed(2)}
      </p>
    </div>
  </foreignobject>
);
`}</code></pre>
    <p>{`This one is very dataset specific. We know each `}<inlineCode parentName="p">{`info`}</inlineCode>{` has a `}<inlineCode parentName="p">{`name`}</inlineCode>{`, a `}<inlineCode parentName="p">{`zipCode`}</inlineCode>{`, and two numbers. Display them in some nice format and that's that.`}</p>
    <p><em parentName="p">{`Each`}</em>{` of our scatterplots now has its own instance of a tooltip. They're all driven by the same state in context, but they're different tooltips.`}</p>
    <p>{`This is crucial.`}</p>
    <h2 {...{
      "id": "fourth-you-need-an-app-to-hold-it-all-together"
    }}>{`Fourth, you need an App to hold it all together`}</h2>
    <p>{`Our App component holds everything together. Generates data, renders scatterplots, sets up a context provider. Everything.`}</p>
    <p>{`Looks like this`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function App() {
  // Generate random data on first load
  const data = useMemo(
    () =>
      range(100).map(i => ({
        id: i,
        number1: Math.random(),
        number2: Math.random(),
        name: Faker.name.findName(),
        zipCode: Faker.address.zipCode(),
        state: Faker.address.state(),
        color: Faker.internet.color()
      })),
    []
  );

  // bucketize states into 50 buckets
  const stateToNumber = scaleOrdinal()
    .domain(data.map(d => d.state))
    .range(range(50));

  // alphabetize letters into 25 buckets
  const alphabet = scaleOrdinal()
    .domain("abcdefghijklmnoprstuvwxyz".split(""))
    .range(range(25));

  const state = useTooltip();

  return (
    <div class="App">
      <h1>React & D3 dashboard with synced tooltips</h1>
      <h2>For simplicity, it's scatterplots ✌️</h2>
      <tooltipcontext class="provider" value={state}>
        <svg width="800" height="600">
          <scatterplot x={0} y={0} width={200} height={200} data={data} xdimension="{d" ==""> d.number1}
            yDimension={d => d.number2}
          />

          <scatterplot x={250} y={0} width={200} height={200} data={data} xdimension="{d" ==""> d.number1}
            yDimension={d => stateToNumber(d.state)}
          />

          <scatterplot x={0} y={250} width={200} height={200} data={data} xdimension="{d" ==""> stateToNumber(d.state)}
            yDimension={d => alphabet(d.name[0])}
          />
        </scatterplot></scatterplot></scatterplot></svg>
      </tooltipcontext>
    </div>
  );
}
`}</code></pre>
    <p>{`So that's a long ass function. This is our hook-based future I'm afraid. Surely patterns will develop to clean this stuff up?`}</p>
    <p>{`But also we were just playing around. No need to make it clean :)`}</p>
    <p>{`We start with some fake data. `}<inlineCode parentName="p">{`useMemo`}</inlineCode>{` makes sure we calculate it once and keep it in memory through any re-renders. Makes our dataviz look more consistent.`}</p>
    <p>{`Two ordinal scales help us turn state names and zip codes into linear numbers. Easier than changing our scatterplots to support ordinal data.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`useTooltip`}</inlineCode>{` hook from earlier gives us a state object that we'll feed into our `}<inlineCode parentName="p">{`tooltipContext`}</inlineCode>{`. Again a good opportunity to make this easier to use, but need to play around some more.`}</p>
    <p>{`For the rendering, we wrap our dashboard with `}<inlineCode parentName="p">{`tooltipContext.Provider`}</inlineCode>{`, give it our state, render an SVG element, and plop down three scatterplots.`}</p>
    <p>{`This is where making scatterplots flexible comes handy. We're reusing the same component, giving it different params, and it gives us different insights into the same dataset.`}</p>
    <p>{`The result, a dashboard with synchronized tooltips.`}</p>
    <p><img parentName="p" {...{
        "src": "/1aa3fb5d20e299a630b9a899cc3bd319/hi9Ipxl.gif",
        "alt": "Scatterplots with synchronized tooltips"
      }}></img></p>
    <p>{`Scatterplots with synchronized tooltips`}</p>
    <p>{`✌️`}</p>
    <h1 {...{
      "id": "in-other-news"
    }}>{`In other news`}</h1>
    <p>{`I DID IT I FINISHED EDITING ALL EIGHT HOURS OF VIDEO FOR React For Data Visualization `}{`\\`}{`o/`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1089782724510965760"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "138.11659192825113%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'553\\'%20viewBox=\\'0%200%20400%20553\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M32%208c-2%201-7%207-8%2010l2%2011%201%201c0%202%205%206%2010%206%204%201%209%200%2012-3%201-2%201-2-1-2-4%200-5-3-2-5l3-1%203-2c2-2%202-2%201-6l-4-6-4-3H32m42%2041c-2%202-2%202-4%201l-2-2v3c0%203%200%203-2%203s-3%201-3%202c0%204%202%206%203%203h2c2%201%205-1%203-3l2-1%202%203c1%202%206%204%206%202l1-2c0-1%200-2-2-1-3%200-3%200-3-4%200-6-1-7-3-4m22%207c1%207%202%208%207%206%207-3%205-11-3-12h-4v6m19%200c0%206%201%207%203%207%203%200%208-4%208-7s-4-6-8-6h-3v6m120%201c0%206%200%206%203%206%204%200%207-3%207-7s-2-6-7-6h-3v7m29-1l1%207%203-1c10%200%209-12%200-12h-4v6M135%2071c-3%202-3%208%200%2010%203%203%205%202%207%200%205-5-1-14-7-10m54%201c-4%204-1%2011%204%2011%202%200%206-4%206-7%200-5-7-8-10-4m45%204l1%207%203-1c10%200%209-12-1-12h-3v6m24-4c-2%205%201%2011%205%2011%203%200%206-3%206-6%200-7-7-9-11-5M73%2092c-3%203-2%207%200%209%203%203%207%203%209-2%203-6-5-13-9-7m119%205v6h4c10-2%209-13%200-13h-4v7m-39%20396c-4%202-5%209-2%2012%203%202%206%203%206%200l3-2c2%200%203%200%203-3%200-5-5-9-10-7m54%200c-4%202-5%207-2%2011%202%203%205%204%207%201h2c1%202%203%200%203-3%201-4%200-6-3-8s-4-2-7-1m39%203c0%204-2%207-5%207-2-1-1%200%201%203%203%203%2012%201%2012-3h-2l-1-3v-3l-1-1h2l-1-2c-4-3-6-3-5%202m15-2l-2%202h4l4%201h-5l-4%201c-1%205%202%209%205%209%202%200%202-1%201-2l1-3h2l1%201v2l-1%202%204-2c3-5%200-13-6-13l-4%202m-128%201c-3%203-3%208%200%2010%204%204%209%202%2012-4%201-1-1-7-4-7-3-2-6-1-8%201m37%200c-2%202-2%2010%200%2011%203%202%206%201%206%200-1-1%200-2%201-3l1-3c0-3%202-2%202%200%201%202%201%202%201%200%200-6-8-9-11-5m-49%201v4l-4%201-1-3v-3c-2%200-3%202-2%206%201%208%2011%208%2012%200%200-5-1-7-4-7-2%200-2%200-1%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/19ccf7a5394a5d0c1250ae9013271594/ca0a1/techletter.app-screenshot-1548697227934.webp 223w", "/static/19ccf7a5394a5d0c1250ae9013271594/75680/techletter.app-screenshot-1548697227934.webp 445w", "/static/19ccf7a5394a5d0c1250ae9013271594/d3be9/techletter.app-screenshot-1548697227934.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/19ccf7a5394a5d0c1250ae9013271594/e92b6/techletter.app-screenshot-1548697227934.png 223w", "/static/19ccf7a5394a5d0c1250ae9013271594/e66bf/techletter.app-screenshot-1548697227934.png 445w", "/static/19ccf7a5394a5d0c1250ae9013271594/e85cb/techletter.app-screenshot-1548697227934.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/19ccf7a5394a5d0c1250ae9013271594/e85cb/techletter.app-screenshot-1548697227934.png",
                "alt": "https://twitter.com/Swizec/status/1089782724510965760",
                "title": "https://twitter.com/Swizec/status/1089782724510965760",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1089782724510965760`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Only took 4 months, heh 😅`}</p>
    <p>{`But now we know. This is a good datapoint for the future.`}</p>
    <p>{`The process started in August with research, writing, running online workshops to test the material and flow. Well, the `}<em parentName="p">{`real`}</em>{` process started in April 2015 with the first React + D3 book …`}</p>
    <p>{`Fingers crossed it doesn't take me 4 years to produce the next full video course 🤞 I think the 5 month flow is doable.`}</p>
    <p>{`Right now we've got 94 videos in 27 sections and 133 lessons. Today I'm recording a couple explainer intro videos to give context and ease onboarding and then we're off to the races launching this thing.`}</p>
    <p>{`Thanks everyone for your patience. Watch your emails for more info coming soon :)`}</p>
    <p>{`Oh also I started ramping up for another marathon because I'm dumb`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1089614535047110656"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "126.00896860986548%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'504\\'%20viewBox=\\'0%200%20400%20504\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M32%2020c-2%201-7%207-8%2010l2%2011%201%201c0%202%206%206%2010%207%203%200%205%200%208-2%206-2%206-4%202-4-3%200-4-3-1-5l3-1%203-2c2-2%202-2%201-6-3-8-13-12-21-9m94%2042c-4%204-1%2011%204%2011%204%200%207-8%203-11-2-3-5-2-7%200m-99%2084l-2%205c0%203%200%204%205%204l6-2h2l6-1c2-1%205-2%206-1l3-1c1-2%203-2%208-1h6l5%201%202%201h4l3-2%205-1c1-1%2012-1%2013%201l5%201%205%203c2%202%2012%201%2014-1l9-1%209-1c2-1%2012-1%2013%201%202%201%206%201%208-1h9l6-1%2011-1h2l9%201h18c5-1%206-1%208%202%201%201%202%202%203%201l2%201-2%202c-2%200-2%201-1%203s3%202%204%201l3%201h18c0%202%202%204%205%204l2%201%205%201%205%202c1%203%2018%200%2018-3l-1-1v-1l2%201c1%202%206%200%206-2%200-1%201-2%202-1l5-1c2-1%202-1-4-1-2-1-1-3%201-3l5-1%208-3%206-2%208-1c7%200%208%200%207%201-1%202%200%205%201%203l6-1%207-2%2011-2%204%201%204%202h4c0%202%200%202%202%200%203-1%203-1%203%201%201%201%201%201%201-1l-1-5c-3-3-344-3-347%200m107%2039v38c0%2036%200%2038-2%2037l-7-1-10-2c-5-3-15-3-21-1-8%203-14%200-9-4h-2c-3%200-3%200-3-2%200-3-2-4-5-2-2%201-3%202-2%203%200%204-1%204-8%204-5%200-7-1-8-2h-1l-2%201v-1l-1-1-2%201c-2%202-4%201-5-2-2-3-8-4-8-2%200%203-9%204-10%202h-1l-1%201c-1%200-2%2014-2%2076v75l2%203%203%202h342l3-2%202-3v-38l-1-61v-23l-33%201c-34%200-37%200-38%202h-1l-1-2h-3l1-1%202-1-1-1c-2%200-1-8%201-9v-1c-2%200-2%200-1-6%202-7%200-11-3-12-5-1-7-9-4-13%202-3%206-13%206-17%200-17-22-30-48-29-16%201-28%206-40%2017-12%2010-15%2022-10%2033%202%204%203%206%202%2010%200%206-1%207-8%207h-5l2%202c1%202%202%203%201%204-2%201-4-2-4-6l-1-4h-5l1%201%202%201h-2l-1%201v5c0%202-1%203-2%203a355%20355%200%2000-26-4c-3%200-4-5-4-18a946%20946%200%2000-3-59h-15m3%208v9c10%202%2011%201%2011-5v-5h-5l-6%201m0%2017v6c0%203%201%204%203%204%207%201%208-1%208-6v-5h-5l-6%201m3%2017c-3%202-2%2013%201%2014%205%201%207-1%207-6%200-8-3-11-8-8m-1%2024l-1%206v4h10v-4c0-8-1-9-5-9-2%200-3%201-4%203m141%203c-5%200-14%203-14%204h6l13%202c8%201%209%201%209-1%200-4-7-6-14-5M79%20282c0%2021%201%2036%203%2036%201%200%202-1%202-3%201-4%201-4%205-4l5-1c2-1%201-1-5-1h-5v-11h5a278%20278%200%200127-2l-1-1-2-1-3-1c-8%201-16%200-15-1l-5-1c-6%200-7-1-7-8l-1-4v-1c2%200%201-3-1-3s-2%201-2%207m195%209h-3c-1-1-4%201-4%203l8%208c2%201%202%203%202%205%200%204%202%205%203%201a158%20158%200%20015-8%20211%20211%200%20010-6c-1%201-1%201-2-1-1-3-2-3-4-3h-3c0-1-2-1-2%201m30%2049l-1%201-1%202c0%202%200%202-2%201-1-2-2-2-3%200v4c-2%200-2%201-1%203%200%203%204%202%205%200l2-2%202-1c0-1%201-2%202-1l3-3%202-3-3-2c-5-1-6-1-5%201M80%20444c-2%201-5%208-3%2010l6%201c6%201%206%200%207-2%202-6-4-11-10-9m16%201c-3%204-3%208%200%2011%205%204%2013%201%2013-4%200-2%200-2-3%201-4%205-7%205-7%201%200-2%200-2%202-2l3-1%202-1c3-1%203-4%200-5-3-3-7-2-10%200m20-1c-4%202-5%207-2%2011s7%204%208%200c0-2%201-3%203-3s2-1%201-4c-1-2-6-6-7-5l-3%201m55%200c-4%202-6%2010-2%2011l2%201h1l1%201c-2%202%205%201%207-2%203-3%203-5%200-8s-6-4-9-3m-20%202c-2%202-2%204%201%204%202%200%201%203-1%204s0%204%205%204c4%200%207-2%207-5%200-1-3-3-4-2l-1-1%202-2-2-2h-7m38%201c-2%202-2%202-1%204v1c-2%200-2%201%200%204%201%203%207%203%209%200%202-2%203-6%201-6h-1l1-1c2%200%201-2-2-3-5-1-5-1-7%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/977d95386892fcfab518e51b6fc1631f/ca0a1/techletter.app-screenshot-1548697229491.webp 223w", "/static/977d95386892fcfab518e51b6fc1631f/75680/techletter.app-screenshot-1548697229491.webp 445w", "/static/977d95386892fcfab518e51b6fc1631f/d3be9/techletter.app-screenshot-1548697229491.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/977d95386892fcfab518e51b6fc1631f/e92b6/techletter.app-screenshot-1548697229491.png 223w", "/static/977d95386892fcfab518e51b6fc1631f/e66bf/techletter.app-screenshot-1548697229491.png 445w", "/static/977d95386892fcfab518e51b6fc1631f/e85cb/techletter.app-screenshot-1548697229491.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/977d95386892fcfab518e51b6fc1631f/e85cb/techletter.app-screenshot-1548697229491.png",
                "alt": "https://twitter.com/Swizec/status/1089614535047110656",
                "title": "https://twitter.com/Swizec/status/1089614535047110656",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1089614535047110656`}</figcaption>{`
  `}</figure></a></p>
    <h1 {...{
      "id": "a-few-til-lessons-from-the-week"
    }}>{`A few TIL lessons from the week`}</h1>
    <p>{`These days I post a cool tip in the form of a TodayILearned on twitter every day. Here are some good ones from this week. Click on them, they're threads 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1087591170174115845"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "86.09865470852019%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'345\\'%20viewBox=\\'0%200%20400%20345\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2021c-5%203-7%208-7%2014l2%206%201%201c0%205%2013%208%2019%205%204-2%205-4%202-4l-4-2c0-1%202-4%204-4l4-2c3-3%201-9-3-12l-4-3c-2-2-10-1-14%201m152%20273c-5%204-4%2012%200%2011h3l7-1c1-2%200-7-2-9s-6-3-8-1m17%201c-3%203-3%208%200%2011%204%203%206%203%207-1l3-3c4%200%202-6-2-8-5-1-6-1-8%201m19%201c-5%205-2%2012%204%2012%207%200%209-8%204-13-3-3-5-2-8%201m17-1c-4%205-1%2013%205%2013%204%200%208-4%208-7%200-7-8-10-13-6m18%200c-4%204-1%2013%203%2011l2%201h5c1-1%201-2-1-3-1-1-1-1%201-2%204-1%204-3%201-6-3-4-8-4-11-1m22-1c-4%201-6%203-6%207%200%203%205%207%208%207%206%200%209-9%205-12-2-2-3-3-7-2m-110%201l-1%203-1%202c-3%200-2%205%200%206%205%204%2013%200%2012-6-1-3-3-4-4-1-1%202-1%202-2-1-2-4-3-5-4-3\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37f47146ae8af6e6b899ff985b828d74/ca0a1/techletter.app-screenshot-1548697329109.webp 223w", "/static/37f47146ae8af6e6b899ff985b828d74/75680/techletter.app-screenshot-1548697329109.webp 445w", "/static/37f47146ae8af6e6b899ff985b828d74/d3be9/techletter.app-screenshot-1548697329109.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37f47146ae8af6e6b899ff985b828d74/e92b6/techletter.app-screenshot-1548697329109.png 223w", "/static/37f47146ae8af6e6b899ff985b828d74/e66bf/techletter.app-screenshot-1548697329109.png 445w", "/static/37f47146ae8af6e6b899ff985b828d74/e85cb/techletter.app-screenshot-1548697329109.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/37f47146ae8af6e6b899ff985b828d74/e85cb/techletter.app-screenshot-1548697329109.png",
                "alt": "https://twitter.com/Swizec/status/1087591170174115845",
                "title": "https://twitter.com/Swizec/status/1087591170174115845",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1087591170174115845`}</figcaption>{`
  `}</figure></a></p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1087973312288952322"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "139.46188340807174%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'558\\'%20viewBox=\\'0%200%20400%20558\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2021c-5%203-7%208-7%2013l2%207%201%201%202%202c2%203%204%202%202%200-3-3-3-8-1-8%203%200%205-3%205-6-1-2%200-3%201-3l1%203c0%204%202%206%205%206s3%200%200%205c-1%203-3%205-5%205l-3%202h9c5-2%208-5%204-5l-3-1c0-3%202-6%205-6h2l2-2c2-2%200-9-3-10l-4-3c-3-3-10-3-15%200m44%20253c-2%2014-1%20175%200%20176l144%201h143V268l-144-1H75v7m84%20234c-5%206%201%2015%208%2012l2-2%201-1c4%200%201-8-3-10-4-1-5-1-8%201m73%201c-2%202-1%205%201%203l2%201-2%201h-3c0%203%200%203%202%205%205%205%2012%201%2012-5l-1-4c-1-1-1%200-1%201l-1%202v-2c2-5-5-7-9-2m19-1c-3%202-4%208-2%2010l1-1%201-2%201%201c0%201%201%202%202%201l1-1%202-1c1%201%203%200%204-1l2-2-3-3c-3-4-6-4-9-1m-54%200c-3%202-3%208%200%2011l2%201h5c5-5-1-16-7-12\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/78b812aeda5a510ffbb383d22b1bc517/ca0a1/techletter.app-screenshot-1548697229416.webp 223w", "/static/78b812aeda5a510ffbb383d22b1bc517/75680/techletter.app-screenshot-1548697229416.webp 445w", "/static/78b812aeda5a510ffbb383d22b1bc517/d3be9/techletter.app-screenshot-1548697229416.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/78b812aeda5a510ffbb383d22b1bc517/e92b6/techletter.app-screenshot-1548697229416.png 223w", "/static/78b812aeda5a510ffbb383d22b1bc517/e66bf/techletter.app-screenshot-1548697229416.png 445w", "/static/78b812aeda5a510ffbb383d22b1bc517/e85cb/techletter.app-screenshot-1548697229416.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/78b812aeda5a510ffbb383d22b1bc517/e85cb/techletter.app-screenshot-1548697229416.png",
                "alt": "https://twitter.com/Swizec/status/1087973312288952322",
                "title": "https://twitter.com/Swizec/status/1087973312288952322",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1087973312288952322`}</figcaption>{`
  `}</figure></a></p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1088683833321390081"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "76.68161434977578%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'307\\'%20viewBox=\\'0%200%20400%20307\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2021c-9%206-9%2019-1%2025%205%203%2012%204%2017%201%203-2%203-4%200-4l-3-2c0-2%202-4%205-4l2-1%201-1%202-1-4-10-3-2c-3-3-11-4-16-1m31%2046v6h3c5%200%207-2%207-7%200-3-3-6-7-6-3%200-3%200-3%207m48%20189v3h-1c0-2%200-2-2-1-2%203-1%206%201%209%202%202%202%202%203%201h8c4-5%201-13-6-13l-3%201m56%200c-9%203-5%2016%205%2014%204-2%206-10%201-13-2-2-3-2-6-1m16%201c-7%205%200%2015%207%2012%203-1%204-4%203-6l-2-3c0-5-4-6-8-3m18%200c-4%204-4%2011%200%2010h8c4%201%205-7%201-10-2-3-7-3-9%200m18%200c-2%202-2%202%200%202s2%200%201%201h-3l-1%203c0%202%201%202%203%202%203%200%204%201%202%203s3%202%206%200c2-1%202-1%201-2l-2-3h2c3%204%204-1%201-5s-8-4-10-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4a44794f9b3a78a5a46695e1716c9120/ca0a1/techletter.app-screenshot-1548697227905.webp 223w", "/static/4a44794f9b3a78a5a46695e1716c9120/75680/techletter.app-screenshot-1548697227905.webp 445w", "/static/4a44794f9b3a78a5a46695e1716c9120/d3be9/techletter.app-screenshot-1548697227905.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4a44794f9b3a78a5a46695e1716c9120/e92b6/techletter.app-screenshot-1548697227905.png 223w", "/static/4a44794f9b3a78a5a46695e1716c9120/e66bf/techletter.app-screenshot-1548697227905.png 445w", "/static/4a44794f9b3a78a5a46695e1716c9120/e85cb/techletter.app-screenshot-1548697227905.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/4a44794f9b3a78a5a46695e1716c9120/e85cb/techletter.app-screenshot-1548697227905.png",
                "alt": "https://twitter.com/Swizec/status/1088683833321390081",
                "title": "https://twitter.com/Swizec/status/1088683833321390081",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1088683833321390081`}</figcaption>{`
  `}</figure></a></p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1089384771510128640"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "86.99551569506727%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'348\\'%20viewBox=\\'0%200%20400%20348\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M35%2019l-7%204c-3%204-3%204-3%2011l1%207%201%202c0%201%206%205%2010%205%204%201%209%200%2012-3%201-2%201-2-1-2-4%200-5-3-2-5l3-1%203-2c2-2%202-2%201-6-3-7-11-12-18-10m27%20268v6h3c5%200%207-2%205-5v-3c1-3-2-5-5-5s-3%200-3%207\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d5aafb13bc5080b39e351996d84ec311/ca0a1/techletter.app-screenshot-1548697230580.webp 223w", "/static/d5aafb13bc5080b39e351996d84ec311/75680/techletter.app-screenshot-1548697230580.webp 445w", "/static/d5aafb13bc5080b39e351996d84ec311/d3be9/techletter.app-screenshot-1548697230580.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d5aafb13bc5080b39e351996d84ec311/e92b6/techletter.app-screenshot-1548697230580.png 223w", "/static/d5aafb13bc5080b39e351996d84ec311/e66bf/techletter.app-screenshot-1548697230580.png 445w", "/static/d5aafb13bc5080b39e351996d84ec311/e85cb/techletter.app-screenshot-1548697230580.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d5aafb13bc5080b39e351996d84ec311/e85cb/techletter.app-screenshot-1548697230580.png",
                "alt": "https://twitter.com/Swizec/status/1089384771510128640",
                "title": "https://twitter.com/Swizec/status/1089384771510128640",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1089384771510128640`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This one has some cool CompSci fundamentals ^`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1089746524748140544"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "125.1121076233184%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'500\\'%20viewBox=\\'0%200%20400%20500\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2021c-4%202-7%207-7%2011%200%205%201%209%203%209l1%202%201%202%201-2-2-4c0-1%201-2%203-2%203-1%205-4%204-8-1-1%200-2%201-2l1%203%201%205c1%202-2%204-4%203l-2%201c-1%202%201%204%202%202h3c2%200%202-1%202-2%200-2%204-4%205-3l-5%209-4%202%202%201c3%201%204%201%208-1s5-4%202-4c-2%200-3-1-3-2%200-2%204-5%206-4l1-1%202-2c2-2-1-10-4-11l-4-2c-3-3-9-3-14%200m47%20298l1%2080h282V240l-142-1H78v80m95%20131c-2%202-3%208-1%2010l1-1%201-2%201%201%202%201%201-1%201-1c2%201%206-1%206-3%200-5-8-8-12-4\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8735747660c30c64210e3d747e9b2b6c/ca0a1/techletter.app-screenshot-1548697228861.webp 223w", "/static/8735747660c30c64210e3d747e9b2b6c/75680/techletter.app-screenshot-1548697228861.webp 445w", "/static/8735747660c30c64210e3d747e9b2b6c/d3be9/techletter.app-screenshot-1548697228861.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/8735747660c30c64210e3d747e9b2b6c/e92b6/techletter.app-screenshot-1548697228861.png 223w", "/static/8735747660c30c64210e3d747e9b2b6c/e66bf/techletter.app-screenshot-1548697228861.png 445w", "/static/8735747660c30c64210e3d747e9b2b6c/e85cb/techletter.app-screenshot-1548697228861.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/8735747660c30c64210e3d747e9b2b6c/e85cb/techletter.app-screenshot-1548697228861.png",
                "alt": "https://twitter.com/Swizec/status/1089746524748140544",
                "title": "https://twitter.com/Swizec/status/1089746524748140544",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1089746524748140544`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This one is an epic mystery with a weird resolution`}</p>
    <h1 {...{
      "id": "a-few-cool-things"
    }}>{`A few cool things`}</h1>
    <p>{`And as always on a Monday, some cool stuff I discovered this week 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://script-8.github.io/"
      }}>{`Script-8`}</a>{` which is a whole 8-bit computer built in JavaScript. Great for making retro games.`}</p>
    <p>{`dmaydan published `}<a parentName="p" {...{
        "href": "https://github.com/dmaydan/Maze_Solver_Generator"
      }}>{`this neat maze builder and solver`}</a>{` that renders on canvas. If you find that cool, you'll enjoy Bostock's epic `}<a parentName="p" {...{
        "href": "https://bost.ocks.org/mike/algorithms/#maze-generation"
      }}>{`maze generation algorithms`}</a>{` article.`}</p>
    <p>{`This is C++ but I really liked it: A `}<a parentName="p" {...{
        "href": "https://github.com/ssloy/tinykaboom/wiki"
      }}>{`KABOOM animation in 180 lines of C++`}</a>{`. Neat effect and good writeup on how to get there.`}</p>
    <p>{`And I think this is a cool idea, but I haven't had time to listen to the episodes. `}<a parentName="p" {...{
        "href": "https://www.reddit.com/r/reactjs/comments/akf1bb/i_created_a_react_learning_course_made_of_podcast/?st=JRFLRUI0&sh=697a92d8"
      }}>{`A React learning course compiled out of podcasts`}</a></p>
    <p>{`Hope you have a great week. and to that person who asked `}<em parentName="p">{`Do you even read these?`}</em>{` in the typeform below: Yes I do but I can't reply.`}</p>
    <p>{`Cheers,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      